import JitsiMeetJS, { JitsiConference, JitsiRemoteTrack } from 'lib-jitsi-meet'

import { EventChannel, SagaIterator } from 'redux-saga'
import { put, takeEvery, takeLatest, select, call, fork, all, delay } from 'redux-saga/effects'

import { convertServiceUserToPluginUser } from '@tabeeb/services/dataConverter'
import { getIsSoundNotificationOnCallJoinEnabled } from '@tabeeb/modules/appConfigState/selectors'
import { RecordingType, ServerRecordingState } from '@tabeeb/enums'
import { enableGridView } from '@tabeeb/modules/gridView/actions'
import { isMobileOnly } from 'react-device-detect'
import { isCurrentUserRecorder } from '@tabeeb/modules/recording/services/localRecordingService'
import { getRecordingState, getSelectedUserId } from '@tabeeb/modules/recording/selectors'
import { getIsGridViewActive } from '@tabeeb/modules/gridView/selectors'
import { getBrowserType } from '@tabeeb/shared/browser'
import * as conferenceActions from '../actions/conference'
import * as trackActions from '../actions/tracks'
import * as jitsiTrackActions from '../actions/jitsiTrack'
import * as snapshotActions from '../../presentationToolbar/actions/snapshot'
import bindJitsiEvent from '../services/events'
import bindJitsiCommand from '../services/commands'
import { usersActions, usersSelectors } from '../../../users'
import * as muteAudioActions from '../../presentationToolbar/actions/muteAudio'
import * as muteVideoActions from '../../presentationToolbar/actions/muteVideo'
import { recordingActions, recordingSelectors } from '../../recording'
import { signalrEvents, signalrActions, signalrConstants } from '../../signalr'

import * as notificationActions from '../../notification/actions'
import { accountSelectors } from '../../account'
import { getCurrentUserId, getLargeVideoUser, getParticipantIdByUserId, getSelectedUser } from '../selectors'
import { contentStateSelectors } from '../../shared/content'
import { leaveVideoCall } from '../../whiteboard/services'
import { showSpeaker, updateAudioLevelIndicatorForUser } from '../services/audioLevelService'
import * as presentationSelectors from '../selectors'
import WatchRTC from '../services/watchRTC/WatchRTC'
import { toggleLargeVideo } from '../actions/tracks'
import { TabeebConnectEvents } from '../constants'

let externalEventChannels: EventChannel<unknown>[] = []
let jitsiCommandChannels: EventChannel<unknown>[] = []
let mobilePriorityViewEventChannels: EventChannel<unknown>[] = []

function* initRoom(): SagaIterator {
  const jitsiConnection: ReturnType<typeof presentationSelectors.getJitsiConnection> = yield select(
    presentationSelectors.getJitsiConnection
  )
  if (!jitsiConnection) {
    return
  }

  const roomId: ReturnType<typeof presentationSelectors.getJitsiRoomId> = yield select(
    presentationSelectors.getJitsiRoomId
  )
  const roomName = `${roomId}`

  const jitsiRoom = jitsiConnection.initJitsiConference(roomName, window.config!)

  const { TenantId: tenantId, Id: currentUserId }: ReturnType<typeof accountSelectors.getMe> = yield select(
    accountSelectors.getMe
  )
  WatchRTC.start(roomName, jitsiRoom.myUserId()!, {
    tenantId,
    userId: currentUserId,
  })

  yield put(conferenceActions.setJitsiRoom(jitsiRoom))
  yield put(conferenceActions.bindRoomEvents())

  const currentUserName: ReturnType<typeof accountSelectors.getCurrentUserFullName> = yield select(
    accountSelectors.getCurrentUserFullName
  )
  jitsiRoom.setDisplayName(currentUserName)

  yield put(trackActions.createLocalTracks())
}

function leaveJitsiRoom(jitsiRoom: JitsiConference | null) {
  if (jitsiRoom) {
    jitsiRoom.leave(undefined).catch(() => {})
  }
}

function* leaveRoom(): SagaIterator {
  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )
  yield put(conferenceActions.unBindRoomEvents())
  yield call(leaveJitsiRoom, jitsiRoom)
}

function* onKicked() {
  yield put(
    notificationActions.onAddInfoNotification({ message: 'User with same credentials has joined the video call' })
  )
  yield delay(2500)

  yield leaveVideoCall()
}

function* bindRoomEvents(): SagaIterator {
  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )
  if (!jitsiRoom) {
    return
  }

  const jitsiRoomEvents = JitsiMeetJS.events.conference

  yield fork(
    bindJitsiEvent,
    jitsiRoom,
    jitsiRoomEvents.TRACK_ADDED,
    conferenceActions.onTrackAdded,
    externalEventChannels
  )

  yield fork(
    bindJitsiEvent,
    jitsiRoom,
    jitsiRoomEvents.CONFERENCE_JOINED,
    conferenceActions.onConferenceJoined,
    externalEventChannels
  )

  yield fork(
    bindJitsiEvent,
    jitsiRoom,
    jitsiRoomEvents.CONFERENCE_FAILED,
    conferenceActions.onConferenceFailed,
    externalEventChannels
  )

  yield fork(bindJitsiEvent, jitsiRoom, jitsiRoomEvents.USER_LEFT, conferenceActions.onUserLeft, externalEventChannels)

  yield fork(
    bindJitsiEvent,
    jitsiRoom,
    jitsiRoomEvents.TRACK_REMOVED,
    conferenceActions.onTrackRemoved,
    externalEventChannels
  )

  yield fork(
    bindJitsiEvent,
    jitsiRoom,
    jitsiRoomEvents.USER_JOINED,
    conferenceActions.onUserJoined,
    externalEventChannels
  )

  yield fork(bindJitsiEvent, jitsiRoom, jitsiRoomEvents.KICKED, conferenceActions.onKicked, externalEventChannels)

  yield fork(
    bindJitsiEvent,
    jitsiRoom,
    jitsiRoomEvents.TRACK_AUDIO_LEVEL_CHANGED,
    conferenceActions.trackAudioLevelChanged,
    externalEventChannels
  )

  yield fork(
    bindJitsiEvent,
    jitsiRoom,
    JitsiMeetJS.events.connectionQuality.LOCAL_STATS_UPDATED,
    conferenceActions.localStatsReceived,
    externalEventChannels
  )

  yield fork(
    bindJitsiEvent,
    jitsiRoom,
    JitsiMeetJS.events.connectionQuality.REMOTE_STATS_UPDATED,
    conferenceActions.remoteStatsReceived,
    externalEventChannels
  )

  yield fork(
    bindJitsiCommand,
    jitsiRoom,
    TabeebConnectEvents.ON_RECORDER_VIDEO_ATTACHED,
    recordingActions.onRecordingVideoAttached,
    jitsiCommandChannels
  )

  yield fork(
    bindJitsiCommand,
    jitsiRoom,
    TabeebConnectEvents.SET_REMOTE_USER_VIDEO_STATE,
    muteVideoActions.onRemoteVideoMuted,
    jitsiCommandChannels
  )

  yield fork(
    bindJitsiCommand,
    jitsiRoom,
    TabeebConnectEvents.SET_REMOTE_USER_AUDIO_STATE,
    muteAudioActions.onRemoteAudioMuted,
    jitsiCommandChannels
  )

  yield fork(
    bindJitsiCommand,
    jitsiRoom,
    TabeebConnectEvents.TAKING_SNAPSHOT,
    snapshotActions.onSnapshotRequestReceived,
    jitsiCommandChannels
  )

  yield fork(
    bindJitsiCommand,
    jitsiRoom,
    TabeebConnectEvents.TAKING_SNAPSHOT,
    snapshotActions.onSnapshotCreated,
    jitsiCommandChannels
  )

  yield fork(
    bindJitsiCommand,
    jitsiRoom,
    TabeebConnectEvents.TRACKS_INITIALIZATION_FAILED,
    conferenceActions.onTracksInitializationFailed,
    jitsiCommandChannels
  )
}

function* unBindRoomEvents(): SagaIterator {
  externalEventChannels.forEach((channel) => channel.close())
  jitsiCommandChannels.forEach((channel) => channel.close())
  mobilePriorityViewEventChannels.forEach((channel) => channel.close())
  externalEventChannels = []
  jitsiCommandChannels = []
  mobilePriorityViewEventChannels = []

  yield put(trackActions.unbindTrackEvents())
}

function* showJoinNotification(userId: number): SagaIterator {
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  const isCallStarted: ReturnType<typeof presentationSelectors.getIsCallStarted> = yield select(
    presentationSelectors.getIsCallStarted
  )
  if (!isCallStarted) {
    return
  }

  if (currentUserId === userId) {
    return
  }

  if (isCurrentUserRecorder()) {
    const selectedUserId: ReturnType<typeof getSelectedUserId> = yield select(getSelectedUserId)
    const { startDate, session }: ReturnType<typeof getRecordingState> = yield select(getRecordingState)

    yield put(
      signalrActions.invokeHubAction({
        method: 'RecordingStatusUpdate',
        args: [
          {
            state: ServerRecordingState.processing,
            recordingType: RecordingType.local,
            session,
            selectedUser: selectedUserId,
            startDate: startDate / 1000,
          },
        ],
      })
    )
  }

  const connectedUser: ReturnType<typeof usersSelectors.getUserById> = yield select(
    (state) => usersSelectors.getUserById(state, { Id: userId }) || {}
  )

  const userName = connectedUser.displayName
  if (!userName) {
    return
  }

  yield put(notificationActions.onAddInfoNotification({ message: `${userName} has joined the call.` }))

  const isSoundNotificationOnCallJoin: ReturnType<typeof getIsSoundNotificationOnCallJoinEnabled> = yield select(
    getIsSoundNotificationOnCallJoinEnabled
  )
  if (!isSoundNotificationOnCallJoin) {
    return
  }

  const audio = new Audio(new URL('@tabeeb/assets/audio/userJoined.mp3', import.meta.url).toString())
  audio.play()
}

function* onSignalrConferenceJoined({ payload: [userId, deviceType] }: { payload: [number, string] }): SagaIterator {
  yield call(showJoinNotification, userId)

  const currentUserId: ReturnType<typeof getCurrentUserId> = yield select(getCurrentUserId)
  if (currentUserId !== userId) {
    yield put(usersActions.changeUserDeviceType({ userId, deviceType }))

    const isCurrentUserPresenter: ReturnType<typeof contentStateSelectors.getIsCurrentUserPresenter> = yield select(
      contentStateSelectors.getIsCurrentUserPresenter
    )
    const isRecordingActive: ReturnType<typeof recordingSelectors.getIsRecordingActive> = yield select(
      recordingSelectors.getIsRecordingActive
    )
    if (isCurrentUserPresenter && isRecordingActive) {
      yield put(usersActions.updateRecordingStatus())
    }
  }
}

function* onTrackAdded({ payload: track }: ReturnType<typeof conferenceActions.onTrackAdded>): SagaIterator {
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  const userId = track.isLocal()
    ? currentUserId
    : yield select((state) => presentationSelectors.getUserIdByParticipantId(state, track.getParticipantId()))

  if (!track.isLocal() && userId !== currentUserId) {
    yield put(trackActions.addRemoteTrack({ track: track as JitsiRemoteTrack, userId }))
    yield put(trackActions.bindTrackEvents(track))
  }

  yield put(trackActions.attachTrack({ track, userId }))
}

function* onTrackRemoved({ payload: track }: ReturnType<typeof conferenceActions.onTrackRemoved>): SagaIterator {
  yield put(jitsiTrackActions.detachTrack(track))
}

function* onUserLeft({ payload: [participantId] }: ReturnType<typeof conferenceActions.onUserLeft>): SagaIterator {
  const userId: ReturnType<typeof presentationSelectors.getUserIdByParticipantId> = yield select((state) =>
    presentationSelectors.getUserIdByParticipantId(state, participantId)
  )
  if (!userId) {
    return
  }

  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )
  if (userId === currentUserId) {
    return
  }

  const usersParticipantId: ReturnType<typeof getParticipantIdByUserId> = yield select((state) =>
    getParticipantIdByUserId(state, userId)
  )
  if (participantId !== usersParticipantId) {
    return
  }

  // reset large video for left user
  const largeVideoUser: ReturnType<typeof getLargeVideoUser> = yield select(getLargeVideoUser)
  if (largeVideoUser !== null && largeVideoUser.id === userId) {
    yield put(trackActions.setLargeVideoUser(null))
  }

  yield put(usersActions.removeOnlineUser(userId))
  yield put(usersActions.resetVideoLoadForUser(userId))
  yield put(trackActions.resetTracksForUser(userId))
  yield put(trackActions.detachTracksForUser(userId))
  yield put(recordingActions.onStopRecordingForUser({ userId }))
  yield put(usersActions.onUpdateMobileZoomValue({ userId, mobileZoomValue: 100, mobileZoomMax: 800 }))

  yield call(updateAudioLevelIndicatorForUser, userId, 0)
}

function* onConferenceJoined(): SagaIterator {
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )
  const roomId: ReturnType<typeof presentationSelectors.getJitsiRoomId> = yield select(
    presentationSelectors.getJitsiRoomId
  )

  yield put(usersActions.addOnlineUser(currentUserId))

  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )
  if (jitsiRoom) {
    const myParticipantId = jitsiRoom.myUserId()!
    yield put(conferenceActions.setParticipantIdForUser({ userId: currentUserId, participantId: myParticipantId }))
    const deviceType = getBrowserType()
    yield put(signalrActions.invokeHubAction({ method: 'ConferenceJoined', args: [roomId, currentUserId, deviceType] }))

    if (isMobileOnly) {
      yield put(trackActions.toggleMobilePriorityView(true))
    }
  }
}

function* onConferenceFailed({
  payload: [errorCode, error],
}: ReturnType<typeof conferenceActions.onConferenceFailed>): SagaIterator {
  yield put(conferenceActions.unBindRoomEvents())
  yield put(notificationActions.onAddErrorNotification({ message: 'Failed to connect to video call' }))

  console.error('Failed to connect to video call:', { code: errorCode, error })
}

function* onUserJoined({
  payload: [participantId, jitsiParticipant],
}: ReturnType<typeof conferenceActions.onUserJoined>): SagaIterator {
  const identity = jitsiParticipant.getIdentity() as {
    user: { id: string; name: string }
  }

  const userId = parseInt(identity.user.id, 10)
  if (!userId) {
    return
  }

  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )
  const presenterId: ReturnType<typeof contentStateSelectors.getPresenterId> = yield select(
    contentStateSelectors.getPresenterId
  )
  const usersParticipantId: ReturnType<typeof getParticipantIdByUserId> = yield select((state) =>
    getParticipantIdByUserId(state, userId)
  )

  if (currentUserId === presenterId && usersParticipantId) {
    const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
      presentationSelectors.getJitsiConference
    )
    const message = 'User with same credentials has joined the video call'

    const myParticipantId = jitsiRoom?.myUserId()
    if (myParticipantId === usersParticipantId) {
      jitsiRoom?.kickParticipant(participantId, message)

      yield put(notificationActions.onAddInfoNotification({ message }))
    } else {
      jitsiRoom?.kickParticipant(usersParticipantId, message)
    }
  }

  if (userId === currentUserId) {
    return
  }

  const joinedUser: ReturnType<typeof usersSelectors.getUserById> = yield select((state) =>
    usersSelectors.getUserById(state, { Id: userId })
  )
  if (!joinedUser) {
    const ownerId: ReturnType<typeof contentStateSelectors.getOwnerId> = yield select(contentStateSelectors.getOwnerId)
    const displayName = jitsiParticipant.getDisplayName()
    const user = convertServiceUserToPluginUser({ Id: userId, Name: displayName, isDeleted: false }, { ownerId })

    yield put(usersActions.addUser(user))
  }

  yield put(conferenceActions.setParticipantIdForUser({ userId, participantId }))
  yield put(trackActions.initTracksForUser(userId))
  yield put(usersActions.addOnlineUser(userId))
}

function* muteRemoteVideo({ payload: userId }: ReturnType<typeof muteVideoActions.muteRemoteVideo>): SagaIterator {
  const videoState: ReturnType<typeof presentationSelectors.getIsUserVideoPlaying> = yield select((state) =>
    presentationSelectors.getIsUserVideoPlaying(state, { Id: userId })
  )

  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )

  jitsiRoom?.sendCommandOnce(TabeebConnectEvents.SET_REMOTE_USER_VIDEO_STATE, {
    value: userId,
    attributes: {
      xmlns: `http://jitsi.org/jitmeet/${TabeebConnectEvents.SET_REMOTE_USER_VIDEO_STATE}`,
      videoState: !videoState,
    },
  })
}

function* muteRemoteAudio({ payload: userId }: ReturnType<typeof muteAudioActions.muteRemoteAudio>): SagaIterator {
  const audioState: ReturnType<typeof presentationSelectors.getIsUserAudioPlaying> = yield select((state) =>
    presentationSelectors.getIsUserAudioPlaying(state, { Id: userId })
  )

  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )

  jitsiRoom?.sendCommandOnce(TabeebConnectEvents.SET_REMOTE_USER_AUDIO_STATE, {
    value: userId,
    attributes: {
      xmlns: `http://jitsi.org/jitmeet/${TabeebConnectEvents.SET_REMOTE_USER_AUDIO_STATE}`,
      audioState: !audioState,
    },
  })
}

function* updateRemoteAudioIndicator({
  payload,
}: ReturnType<typeof muteAudioActions.updateRemoteAudioIndicator>): SagaIterator {
  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )

  jitsiRoom?.sendCommandOnce(TabeebConnectEvents.ON_REMOTE_AUDIO_INDICATOR_UPDATED, {
    value: payload,
    attributes: {
      xmlns: `http://jitsi.org/jitmeet/${TabeebConnectEvents.ON_REMOTE_AUDIO_INDICATOR_UPDATED}`,
    },
  })
}

function* onSnapshotTaking(): SagaIterator {
  const selectedUser: ReturnType<typeof getSelectedUser> = yield select(getSelectedUser)
  if (!selectedUser) {
    return
  }

  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )

  jitsiRoom?.sendCommandOnce(TabeebConnectEvents.TAKING_SNAPSHOT, {
    value: selectedUser.id,
    attributes: {
      timestamp: new Date(),
      sourceUserId: currentUserId,
      targetUserId: selectedUser.id,
      xmlns: `http://jitsi.org/jitmeet/${TabeebConnectEvents.TAKING_SNAPSHOT}`,
    },
  })
}

function* onSnapshotCreated(): SagaIterator {
  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )

  jitsiRoom?.sendCommandOnce(TabeebConnectEvents.SNAPSHOT_CREATED, {
    attributes: {
      xmlns: `http://jitsi.org/jitmeet/${TabeebConnectEvents.SNAPSHOT_CREATED}`,
    },
  })
}

function* onTrackMuteChanged(): SagaIterator {
  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )

  jitsiRoom?.sendCommandOnce(TabeebConnectEvents.TRACK_MUTE_UPDATED, {
    attributes: {
      xmlns: `http://jitsi.org/jitmeet/${TabeebConnectEvents.TRACK_MUTE_UPDATED}`,
    },
  })
}

function* onLocalStatsReceived(action: ReturnType<typeof conferenceActions.localStatsReceived>): SagaIterator {
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )
  yield put(usersActions.updateBitrate({ userId: currentUserId, bitrate: action.payload.bitrate }))
  yield put(
    usersActions.updateQuality({
      userId: currentUserId,
      connectionQuality: action.payload.connectionQuality,
    })
  )
}

function* onRemoteStatsReceived({
  payload: [participantId, connectivity],
}: ReturnType<typeof conferenceActions.remoteStatsReceived>): SagaIterator {
  const userId: ReturnType<typeof presentationSelectors.getUserIdByParticipantId> = yield select((state) =>
    presentationSelectors.getUserIdByParticipantId(state, participantId)
  )

  yield put(usersActions.updateBitrate({ userId, bitrate: connectivity.bitrate }))
  yield put(
    usersActions.updateQuality({
      userId,
      connectionQuality: connectivity.connectionQuality,
    })
  )
}

function* onTracksInitializationFailed({
  payload: [participantId],
}: ReturnType<typeof conferenceActions.onTracksInitializationFailed>): SagaIterator {
  const userId: ReturnType<typeof presentationSelectors.getUserIdByParticipantId> = yield select((state) =>
    presentationSelectors.getUserIdByParticipantId(state, participantId)
  )

  yield put(usersActions.resetVideoLoadForUser(userId))
}

function* trackAudioLevelChanged({
  payload: [participantId, audioLevel],
}: ReturnType<typeof conferenceActions.trackAudioLevelChanged>): SagaIterator {
  const userId: ReturnType<typeof presentationSelectors.getUserIdByParticipantId> = yield select((state) =>
    presentationSelectors.getUserIdByParticipantId(state, participantId)
  )

  yield call(updateAudioLevelIndicatorForUser, userId, audioLevel)
}

function* onShowSpeaker({
  payload: [participantId, audioLevel],
}: ReturnType<typeof conferenceActions.showSpeaker>): SagaIterator {
  const userId: ReturnType<typeof presentationSelectors.getUserIdByParticipantId> = yield select((state) =>
    presentationSelectors.getUserIdByParticipantId(state, participantId)
  )
  if (!userId) {
    return
  }

  yield call(showSpeaker, userId, audioLevel)
}

function* enableMobilePriorityView(): SagaIterator {
  const presenterId: ReturnType<typeof contentStateSelectors.getPresenterId> = yield select(
    contentStateSelectors.getPresenterId
  )

  const currentUserId: ReturnType<typeof getCurrentUserId> = yield select(getCurrentUserId)

  const largeVideoUser: ReturnType<typeof getLargeVideoUser> = yield select(getLargeVideoUser)

  if ((!largeVideoUser || largeVideoUser.id !== presenterId) && currentUserId !== presenterId) {
    yield put(trackActions.toggleLargeVideo({ userId: presenterId }))
  }

  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )

  if (!jitsiRoom) {
    return
  }

  const jitsiRoomEvents = JitsiMeetJS.events.conference

  yield fork(
    bindJitsiEvent,
    jitsiRoom,
    jitsiRoomEvents.TRACK_AUDIO_LEVEL_CHANGED,
    conferenceActions.showSpeaker,
    mobilePriorityViewEventChannels
  )
}

function disableMobilePriorityView() {
  mobilePriorityViewEventChannels.forEach((channel) => channel.close())
  mobilePriorityViewEventChannels = []
}

function* onGridViewEnabled(): SagaIterator {
  yield put(trackActions.toggleMobilePriorityView(false))
}

function* toggleMobilePriorityView({
  payload: enablePriorityView,
}: ReturnType<typeof trackActions.toggleMobilePriorityView>): SagaIterator {
  if (!isMobileOnly) {
    return
  }

  if (enablePriorityView) {
    yield call(enableMobilePriorityView)
  } else {
    yield call(disableMobilePriorityView)
  }
}

function* onScreenSharingStarted({ payload: [userId] }: { payload: [number] }): SagaIterator {
  const isCallStarted: ReturnType<typeof presentationSelectors.getIsCallStarted> = yield select(
    presentationSelectors.getIsCallStarted
  )
  if (!isCallStarted) {
    return
  }

  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )
  if (currentUserId === userId) {
    return
  }

  const gridViewActive: ReturnType<typeof getIsGridViewActive> = yield select(getIsGridViewActive)
  if (!gridViewActive) {
    return
  }

  yield put(toggleLargeVideo({ userId }))
}

function* onScreenSharingStopped({ payload: [userId] }: { payload: [number] }): SagaIterator {
  const isCallStarted: ReturnType<typeof presentationSelectors.getIsCallStarted> = yield select(
    presentationSelectors.getIsCallStarted
  )
  if (!isCallStarted) {
    return
  }

  const largeVideoUser: ReturnType<typeof getLargeVideoUser> = yield select(getLargeVideoUser)
  if (largeVideoUser == null) {
    return
  }

  if (largeVideoUser.id !== userId) {
    return
  }

  yield put(enableGridView())
}

function* conferenceSaga() {
  yield all([
    takeLatest(conferenceActions.initRoom, initRoom),
    takeLatest(conferenceActions.leaveRoom, leaveRoom),
    takeLatest(conferenceActions.bindRoomEvents, bindRoomEvents),
    takeLatest(conferenceActions.unBindRoomEvents, unBindRoomEvents),
    takeLatest(conferenceActions.onTrackAdded, onTrackAdded),
    takeLatest(conferenceActions.onTrackRemoved, onTrackRemoved),
    takeLatest(conferenceActions.onKicked, onKicked),
    takeLatest(conferenceActions.onUserLeft, onUserLeft),
    takeLatest(conferenceActions.onConferenceJoined, onConferenceJoined),
    takeLatest(conferenceActions.onConferenceFailed, onConferenceFailed),
    takeLatest(conferenceActions.onUserJoined, onUserJoined),
    takeLatest(conferenceActions.localStatsReceived, onLocalStatsReceived),
    takeLatest(conferenceActions.remoteStatsReceived, onRemoteStatsReceived),
    takeLatest(muteVideoActions.muteRemoteVideo, muteRemoteVideo),
    takeLatest(muteAudioActions.muteRemoteAudio, muteRemoteAudio),
    takeLatest(muteVideoActions.onRemoteVideoMuted, onTrackMuteChanged),
    takeLatest(muteAudioActions.onRemoteAudioMuted, onTrackMuteChanged),
    takeLatest(snapshotActions.onSnapshotTaking, onSnapshotTaking),
    takeLatest(snapshotActions.onSnapshotCreated, onSnapshotCreated),
    takeLatest(muteAudioActions.updateRemoteAudioIndicator, updateRemoteAudioIndicator),
    takeLatest(conferenceActions.onTracksInitializationFailed, onTracksInitializationFailed),
    takeEvery(conferenceActions.trackAudioLevelChanged, trackAudioLevelChanged),
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onConferenceJoined, onSignalrConferenceJoined),
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onScreenSharingStarted, onScreenSharingStarted),
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onScreenSharingStopped, onScreenSharingStopped),
    takeLatest(enableGridView, onGridViewEnabled),
    takeLatest(trackActions.toggleMobilePriorityView, toggleMobilePriorityView),
    takeLatest(conferenceActions.showSpeaker, onShowSpeaker),
  ])
}

export default conferenceSaga
